import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/api";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Chip,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

const accent = "#0d47a1";

function MetaRow({ label, value, valueSx }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={{ ...metaRowStyles.value, ...(valueSx || {}) }} title={value}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

const metaRowStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 1.5,
    py: 0.75,
    borderBottom: "1px solid rgba(0,0,0,0.04)",
    "&:last-of-type": { borderBottom: "none" },
  },
  label: {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: "0.875rem",
    color: "#1e293b",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    maxWidth: "65%",
    textAlign: "right",
  },
};

function MessageIncidentCard({ messageData, onViewImages, onResolve }) {
  const resolved = Boolean(messageData.resolved);
  const preview =
    messageData.message && String(messageData.message).length > 56
      ? `${String(messageData.message).slice(0, 56)}…`
      : messageData.message || "Robot message";

  return (
    <Card elevation={0} sx={styles.messageCard}>
      <CardContent sx={styles.messageCardContent}>
        <Box sx={styles.messageCardHeader}>
          <Typography sx={styles.cardIncidentTitle} title={messageData.message}>
            {preview}
          </Typography>
          <Chip
            size="small"
            label={resolved ? "Resolved" : "Open"}
            icon={
              resolved ? (
                <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={resolved ? styles.chipResolved : styles.chipOpen}
          />
        </Box>

        <Typography sx={styles.sectionLabel}>Details</Typography>
        <Box>
          <MetaRow label="Email ID" value={messageData.emailId} />
          <MetaRow label="Message" value={messageData.message} />
          <MetaRow label="Error Code" value={messageData.ErrorCode} />
          {messageData.Fault && <MetaRow label="Fault" value={messageData.Fault} />}
          {messageData.FaultDetails && (
            <MetaRow label="Fault details" value={messageData.FaultDetails} />
          )}
          {messageData.MapName && (
            <MetaRow label="Map name" value={messageData.MapName} />
          )}
          <MetaRow
            label="Created at"
            value={new Date(messageData.createdAt).toLocaleString("en-GB")}
          />
          <MetaRow
            label="Resolved"
            value={resolved ? "Yes" : "No"}
            valueSx={{
              color: resolved ? "#2e7d32" : "#d32f2f",
              fontWeight: 600,
            }}
          />
          {resolved && (
            <>
              <MetaRow
                label="Resolved message"
                value={messageData.resolvedMessage}
                valueSx={{ color: "#2e7d32" }}
              />
              <MetaRow
                label="Resolved at"
                value={
                  messageData.resolveDate
                    ? new Date(messageData.resolveDate).toLocaleString()
                    : "—"
                }
                valueSx={{ color: "#2e7d32" }}
              />
            </>
          )}
        </Box>

        <Box sx={styles.messageActions}>
          {resolved ? (
            <Typography sx={styles.resolvedNote}>
              This incident is closed. Images were removed after resolution.
            </Typography>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<PhotoCameraOutlinedIcon />}
                onClick={() => onViewImages(messageData._id)}
                sx={styles.actionPrimary}
              >
                View images
              </Button>
              <Button
                variant="contained"
                startIcon={<TaskAltOutlinedIcon />}
                onClick={() => onResolve(messageData._id)}
                sx={styles.actionDanger}
              >
                Mark resolved
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

const RobotMessages = () => {
  const { robotId } = useParams();
  const [messagesData, setMessagesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [resolvedMessage, setResolvedMessage] = useState("");
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`/get-robotmsgs/${robotId}`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        setMessagesData(response.data.data || []);
      } catch (err) {
        console.error("Error loading robot messages:", err);
        if (err?.response?.status === 404) {
          setMessagesData([]);
          setError(null);
        } else {
          setError("Failed to load the robot messages.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [robotId]);

  const handleViewImages = (messageId) => {
    navigate(`/camera-images/${messageId}`);
  };

  const handleDelete = (messageId) => {
    setSnackbarMessage(
      "Enter a short resolution note. Associated images will be removed."
    );
    setSnackbarSeverity("warning");
    setSnackbarOpen(true);
    setSelectedMessageId(messageId);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!resolvedMessage?.trim()) {
      setSnackbarMessage("Please enter a resolved message.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await axios.delete(
        `/robotmsgs-delete/message/${selectedMessageId}/images`,
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
          data: {
            resolvedMessage: resolvedMessage.trim(),
          },
        }
      );

      setMessagesData((prevData) =>
        prevData.map((msg) =>
          msg._id === selectedMessageId
            ? {
                ...msg,
                camera_images: [],
                resolved: true,
                resolvedMessage: resolvedMessage.trim(),
              }
            : msg
        )
      );

      setSnackbarMessage("Incident marked resolved and images removed.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setOpenDialog(false);
      setResolvedMessage("");
    } catch (err) {
      console.error("Error deleting images:", err);
      setError("Failed to delete images.");
    }
  };

  if (loading) {
    return (
      <Box sx={styles.pageWrap}>
        <Box sx={styles.innerWrap}>
          <Box sx={styles.headerSkeleton}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width="55%" height={36} />
              <Skeleton variant="text" width="40%" height={22} />
            </Box>
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton
                  variant="rounded"
                  height={360}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Robot messages</Typography>
            <Typography sx={styles.subtitle}>
              Robot ID <Box component="span" sx={styles.robotIdInline}>{robotId}</Box>
              {" · "}
              Review incidents, view camera images, and close issues
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={styles.errorAlert}>
            {error}
          </Alert>
        )}

        {messagesData.length === 0 ? (
          <Box sx={styles.emptyState}>
            <ChatBubbleOutlineIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>
              No messages for this robot
            </Typography>
            <Typography sx={styles.emptySubtitle}>
              When this robot reports an issue, it will appear here.
            </Typography>
          </Box>
        ) : messagesData.length === 1 ? (
          <Box sx={styles.singleCardWrap}>
            <Box sx={styles.singleCardInner}>
              <MessageIncidentCard
                messageData={messagesData[0]}
                onViewImages={handleViewImages}
                onResolve={handleDelete}
              />
            </Box>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {messagesData.map((messageData) => (
              <Grid item xs={12} sm={6} md={4} key={messageData._id}>
                <MessageIncidentCard
                  messageData={messageData}
                  onViewImages={handleViewImages}
                  onResolve={handleDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setResolvedMessage("");
        }}
        PaperProps={{ sx: styles.dialogPaper }}
        sx={styles.dialogRoot}
      >
        <Box sx={styles.dialogResolveHeader}>
          <Box sx={styles.dialogIconCircleResolve}>
            <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 28, color: "#fff" }} />
          </Box>
          <Typography sx={styles.dialogTitleMain}>Resolve incident</Typography>
          <Typography sx={styles.dialogTitleSub}>
            Confirming will delete stored images for this message. Add a note for
            the record.
          </Typography>
        </Box>
        <DialogContent sx={styles.dialogContent}>
          <Typography sx={styles.dialogFieldLabel}>Resolution note</Typography>
          <TextField
            autoFocus
            margin="dense"
            placeholder="Describe what was fixed or verified…"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            minRows={4}
            value={resolvedMessage}
            onChange={(e) => setResolvedMessage(e.target.value)}
            sx={styles.dialogInput}
            InputProps={{ sx: { borderRadius: 2, bgcolor: "#ffffff" } }}
          />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setResolvedMessage("");
            }}
            sx={styles.dialogCancel}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" sx={styles.dialogConfirm}>
            Confirm and remove images
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const styles = {
  pageWrap: {
    minHeight: "100vh",
    bgcolor: "#fafbfc",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  headerSkeleton: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    lineHeight: 1.25,
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
    lineHeight: 1.5,
  },
  robotIdInline: {
    fontWeight: 600,
    color: "#475569",
  },
  errorAlert: {
    mb: 3,
    borderRadius: 2,
  },
  emptyState: {
    textAlign: "center",
    py: 8,
    px: 2,
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
  },
  emptyIcon: {
    fontSize: 56,
    color: "#cbd5e1",
    mb: 2,
  },
  emptyTitle: {
    color: "#475569",
    fontWeight: 600,
    fontSize: "1.125rem",
  },
  emptySubtitle: {
    color: "#94a3b8",
    fontSize: "0.875rem",
    mt: 1,
    maxWidth: 420,
    mx: "auto",
  },
  singleCardWrap: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  singleCardInner: {
    width: "100%",
    maxWidth: 640,
  },
  messageCard: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.06)",
    minHeight: 320,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
    },
  },
  messageCardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    py: 2.5,
    px: 3,
    "&:last-child": { pb: 2.5 },
  },
  messageCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 1.5,
    mb: 2,
  },
  cardIncidentTitle: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: "#1e293b",
    lineHeight: 1.35,
    flex: 1,
    minWidth: 0,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  chipResolved: {
    fontWeight: 600,
    fontSize: "0.75rem",
    bgcolor: "rgba(46, 125, 50, 0.12)",
    color: "#2e7d32",
    flexShrink: 0,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipOpen: {
    fontWeight: 600,
    fontSize: "0.75rem",
    bgcolor: "rgba(237, 108, 2, 0.12)",
    color: "#ed6c02",
    flexShrink: 0,
    "& .MuiChip-icon": { color: "inherit" },
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    mb: 1,
  },
  messageActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1.5,
    mt: 2,
    pt: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },
  actionPrimary: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 2,
    flex: { xs: "1 1 100%", sm: "1 1 0" },
    minWidth: 0,
    bgcolor: accent,
    boxShadow: "0 2px 8px rgba(13, 71, 161, 0.25)",
    "&:hover": {
      bgcolor: "#0a3d91",
      boxShadow: "0 4px 12px rgba(13, 71, 161, 0.35)",
    },
  },
  actionDanger: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 2,
    flex: { xs: "1 1 100%", sm: "1 1 0" },
    minWidth: 0,
    bgcolor: "#d32f2f",
    boxShadow: "0 2px 8px rgba(211, 47, 47, 0.25)",
    "&:hover": {
      bgcolor: "#b71c1c",
      boxShadow: "0 4px 12px rgba(211, 47, 47, 0.35)",
    },
  },
  resolvedNote: {
    fontSize: "0.875rem",
    color: "#64748b",
    lineHeight: 1.5,
    width: "100%",
    textAlign: { xs: "left", sm: "center" },
  },
  dialogRoot: {
    "& .MuiBackdrop-root": {
      bgcolor: "rgba(15, 23, 42, 0.4)",
      backdropFilter: "blur(2px)",
    },
  },
  dialogPaper: {
    borderRadius: 4,
    p: 0,
    maxWidth: 460,
    width: "100%",
    bgcolor: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 24px 48px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  dialogResolveHeader: {
    px: 3,
    pt: 3,
    pb: 2,
    textAlign: "center",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#f8fafc",
  },
  dialogIconCircleResolve: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    bgcolor: accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1rem",
    boxShadow: "0 4px 14px rgba(13, 71, 161, 0.35)",
  },
  dialogTitleMain: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#1a1a2e",
    lineHeight: 1.3,
  },
  dialogTitleSub: {
    fontSize: "0.875rem",
    color: "#64748b",
    mt: 0.5,
    lineHeight: 1.5,
  },
  dialogContent: {
    px: 3,
    pt: 2.5,
    pb: 2,
    overflow: "visible",
  },
  dialogFieldLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#374151",
    mb: 1,
  },
  dialogInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#ffffff",
      "& fieldset": {
        borderColor: "rgba(0,0,0,0.12)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0,0,0,0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: accent,
        borderWidth: "2px",
      },
    },
  },
  dialogActions: {
    px: 3,
    py: 2.5,
    gap: 1.5,
    flexWrap: "wrap",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fafbfc",
  },
  dialogCancel: {
    textTransform: "none",
    color: "#64748b",
    fontSize: "0.9375rem",
    fontWeight: 600,
    borderRadius: 2,
    px: 2.5,
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.05)",
    },
  },
  dialogConfirm: {
    textTransform: "none",
    fontSize: "0.9375rem",
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.25,
    bgcolor: accent,
    boxShadow: "0 2px 8px rgba(13, 71, 161, 0.3)",
    "&:hover": {
      bgcolor: "#0a3d91",
      boxShadow: "0 4px 12px rgba(13, 71, 161, 0.4)",
    },
  },
};

export default RobotMessages;
